/* Downloadable hall-of-fame share card — pure canvas, no deps.
   1200x630 (social og size), dark wasteland frame, name + tag + points + badge chips. */
export const downloadShareCard = ({ name, tag, points, badges = [] }) => {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // backdrop + frame
  ctx.fillStyle = "#030303";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(255, 0, 51, 0.35)";
  ctx.lineWidth = 3;
  for (let i = 0; i < 14; i += 1) {
    const x = Math.random() * W;
    ctx.beginPath();
    ctx.moveTo(x, -40);
    ctx.lineTo(x - 160, H + 40);
    ctx.stroke();
  }
  ctx.strokeStyle = "#ffc400";
  ctx.lineWidth = 4;
  ctx.strokeRect(24, 24, W - 48, H - 48);
  ctx.strokeStyle = "rgba(255, 196, 0, 0.25)";
  ctx.lineWidth = 2;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  ctx.textAlign = "center";

  // eyebrow
  ctx.fillStyle = "#ff0033";
  ctx.font = "700 26px 'Courier New', monospace";
  ctx.fillText("D O L O ' S  W O R L D  //  H A L L  O F  F A M E", W / 2, 118);

  // callsign
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 96px 'Bebas Neue', 'Arial Narrow', sans-serif";
  ctx.fillText((name || tag).toUpperCase(), W / 2, 252);

  // tag + points
  ctx.fillStyle = "#ff9900";
  ctx.font = "700 34px 'Courier New', monospace";
  ctx.fillText(`${tag} — ${points} PTS`, W / 2, 316);

  // badge chips, centered as a row
  if (badges.length > 0) {
    ctx.font = "800 24px 'Courier New', monospace";
    const padX = 18;
    const gap = 16;
    const chipH = 46;
    const widths = badges.map((b) => ctx.measureText(b).width + padX * 2);
    const total = widths.reduce((a, b) => a + b, 0) + gap * (badges.length - 1);
    let x = (W - total) / 2;
    const y = 360;
    badges.forEach((b, i) => {
      const legend = b.startsWith("30");
      const elite = b.startsWith("14");
      ctx.fillStyle = "rgba(155, 228, 255, 0.08)";
      ctx.strokeStyle = legend ? "#ff0033" : elite ? "#ff9900" : "#9be4ff";
      ctx.lineWidth = 2;
      ctx.fillRect(x, y, widths[i], chipH);
      ctx.strokeRect(x, y, widths[i], chipH);
      ctx.fillStyle = legend ? "#ff5c7a" : elite ? "#ffb322" : "#9be4ff";
      ctx.textAlign = "left";
      ctx.fillText(b, x + padX, y + 31);
      ctx.textAlign = "center";
      x += widths[i] + gap;
    });
  }

  // footer
  ctx.fillStyle = "#8a8a8a";
  ctx.font = "700 24px 'Courier New', monospace";
  ctx.fillText("PLAY THE GAMES. STACK POINTS. GET DROPS FIRST.", W / 2, H - 116);
  ctx.fillStyle = "#ffc400";
  ctx.fillText("DOLOSWORLD.COM", W / 2, H - 72);

  const link = document.createElement("a");
  link.download = `dolo-hall-of-fame-${tag.toLowerCase()}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
};

/* Downloadable Titan Tracker field card — kid's outpost, stats, and a mini radar
   with their titans as blips. Pure canvas, same frame language as the share card. */
export const downloadRadarCard = ({ outpost, titans, total, caught, defenseBest, critters, critterTotal, blips = [], skinColor = "#78ffa0" }) => {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // backdrop + frame
  ctx.fillStyle = "#030303";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(255, 0, 51, 0.35)";
  ctx.lineWidth = 3;
  for (let i = 0; i < 14; i += 1) {
    const x = Math.random() * W;
    ctx.beginPath();
    ctx.moveTo(x, -40);
    ctx.lineTo(x - 160, H + 40);
    ctx.stroke();
  }
  ctx.strokeStyle = skinColor;
  ctx.lineWidth = 4;
  ctx.strokeRect(24, 24, W - 48, H - 48);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
  ctx.lineWidth = 2;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  // eyebrow
  ctx.textAlign = "center";
  ctx.fillStyle = "#ff0033";
  ctx.font = "700 26px 'Courier New', monospace";
  ctx.fillText("D O L O ' S  W O R L D  //  T I T A N  T R A C K E R", W / 2, 112);

  // outpost call sign
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 84px 'Bebas Neue', 'Arial Narrow', sans-serif";
  ctx.fillText((outpost || "HOME RIG").toUpperCase(), W / 2, 232);

  // mini radar (left of center bottom)
  const cx = 330;
  const cy = 430;
  const R = 130;
  ctx.strokeStyle = skinColor;
  ctx.globalAlpha = 0.8;
  [0.33, 0.66, 1].forEach((f) => {
    ctx.beginPath();
    ctx.arc(cx, cy, R * f, 0, Math.PI * 2);
    ctx.stroke();
  });
  for (let i = 0; i < 8; i += 1) {
    const a = (i / 8) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a) * R, cy + Math.sin(a) * R);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  // sweep wedge
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  ctx.arc(cx, cy, R, -0.6, -0.1);
  ctx.closePath();
  ctx.fillStyle = skinColor;
  ctx.globalAlpha = 0.25;
  ctx.fill();
  ctx.globalAlpha = 1;
  // titan blips (scaled from the live radar's polar positions)
  ctx.fillStyle = "#ffcf5c";
  blips.slice(0, 9).forEach(() => {
    const a = Math.random() * Math.PI * 2;
    const d = 0.2 + Math.random() * 0.7;
    ctx.beginPath();
    ctx.arc(cx + Math.cos(a) * R * d, cy + Math.sin(a) * R * d, 7, 0, Math.PI * 2);
    ctx.fill();
  });

  // stats column (right of the radar)
  ctx.textAlign = "left";
  ctx.font = "800 34px 'Courier New', monospace";
  const stats = [
    [`TITANS ADOPTED`, `${titans}/${total}`],
    [`SIGNALS CAUGHT`, `${caught}`],
    [`DEFENSE BEST`, `${defenseBest}`],
    [`CRITTERS FOUND`, `${critters}/${critterTotal}`],
  ];
  stats.forEach(([k, v], i) => {
    const y = 360 + i * 56;
    ctx.fillStyle = "#8a8a8a";
    ctx.fillText(k, 560, y);
    ctx.fillStyle = skinColor;
    ctx.fillText(v, 940, y);
  });

  // footer
  ctx.textAlign = "center";
  ctx.fillStyle = "#8a8a8a";
  ctx.font = "700 24px 'Courier New', monospace";
  ctx.fillText("COLLECT. EXPEDITE. DEFEND.", W / 2, H - 96);
  ctx.fillStyle = "#ffc400";
  ctx.fillText("DOLOSWORLD.COM", W / 2, H - 58);

  const link = document.createElement("a");
  link.download = `dolo-titan-tracker-${(outpost || "home-rig").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
};


/* Weekly field recap card — the kid's Sunday shareable: this week's catches, kills,
   new critters, streak, and defense best on a branded frame. */
export const downloadRecapCard = ({ outpost, week, caught, kills, critters, streak, defenseBest, skinColor = "#78ffa0" }) => {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#030303";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = "rgba(255, 0, 51, 0.35)";
  ctx.lineWidth = 3;
  for (let i = 0; i < 14; i += 1) {
    const x = Math.random() * W;
    ctx.beginPath();
    ctx.moveTo(x, -40);
    ctx.lineTo(x - 160, H + 40);
    ctx.stroke();
  }
  ctx.strokeStyle = "#ffc400";
  ctx.lineWidth = 4;
  ctx.strokeRect(24, 24, W - 48, H - 48);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
  ctx.lineWidth = 2;
  ctx.strokeRect(40, 40, W - 80, H - 80);

  ctx.textAlign = "center";
  ctx.fillStyle = "#ff0033";
  ctx.font = "700 26px 'Courier New', monospace";
  ctx.fillText("D O L O ' S  W O R L D  //  W E E K L Y  F I E L D  R E C A P", W / 2, 108);

  ctx.fillStyle = "#ffffff";
  ctx.font = "900 72px 'Bebas Neue', 'Arial Narrow', sans-serif";
  ctx.fillText((outpost || "HOME RIG").toUpperCase(), W / 2, 205);

  ctx.fillStyle = "#ffc400";
  ctx.font = "900 44px 'Bebas Neue', 'Arial Narrow', sans-serif";
  ctx.fillText(week || "", W / 2, 262);

  const stats = [
    ["SIGNALS CAUGHT THIS WEEK", caught],
    ["MEGA WORMS DOWN", kills],
    ["NEW CRITTERS ADOPTED", critters],
    ["DAILY DROP STREAK", streak],
    ["DEFENSE BEST", defenseBest],
  ];
  ctx.textAlign = "left";
  ctx.font = "800 32px 'Courier New', monospace";
  stats.forEach(([k, v], i) => {
    const y = 330 + i * 52;
    ctx.fillStyle = "#8a8a8a";
    ctx.fillText(k, 190, y);
    ctx.fillStyle = skinColor;
    ctx.textAlign = "right";
    ctx.fillText(String(v), 1010, y);
    ctx.textAlign = "left";
  });

  ctx.textAlign = "center";
  ctx.fillStyle = "#8a8a8a";
  ctx.font = "700 24px 'Courier New', monospace";
  ctx.fillText("COLLECT. EXPEDITE. DEFEND.", W / 2, H - 62);
  ctx.fillStyle = "#ffc400";
  ctx.fillText("DOLOSWORLD.COM", W / 2, H - 30);

  const link = document.createElement("a");
  link.download = `dolo-weekly-recap-${(week || "week").toLowerCase()}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
};



/* Downloadable Dolos Bingo victory card — orange wasteland frame inspired by
   the supplied Wasteland Bingo key art. Pure canvas, 1200x630 social size. */
export const downloadBingoVictoryCard = ({ player = "RUNNER", tag = "GUEST", victory, score, marked, calls }) => {
  const W = 1200;
  const H = 630;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  const victoryLabel = (victory || "BINGO VICTORY").toUpperCase();
  const safeName = (player || "RUNNER").toUpperCase().slice(0, 22);

  const octagon = (x, y, w, h, cut) => {
    ctx.beginPath();
    ctx.moveTo(x + cut, y);
    ctx.lineTo(x + w - cut, y);
    ctx.lineTo(x + w, y + cut);
    ctx.lineTo(x + w, y + h - cut);
    ctx.lineTo(x + w - cut, y + h);
    ctx.lineTo(x + cut, y + h);
    ctx.lineTo(x, y + h - cut);
    ctx.lineTo(x, y + cut);
    ctx.closePath();
  };

  ctx.fillStyle = "#070403";
  ctx.fillRect(0, 0, W, H);
  const sky = ctx.createLinearGradient(0, 0, 0, H);
  sky.addColorStop(0, "rgba(255, 112, 24, 0.22)");
  sky.addColorStop(0.48, "rgba(70, 24, 8, 0.26)");
  sky.addColorStop(1, "rgba(0, 0, 0, 0.8)");
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = "rgba(255, 177, 42, 0.24)";
  ctx.lineWidth = 2;
  for (let x = -H; x < W; x += 48) {
    ctx.beginPath();
    ctx.moveTo(x, H);
    ctx.lineTo(x + H, 0);
    ctx.stroke();
  }

  ctx.strokeStyle = "#ffae24";
  ctx.lineWidth = 7;
  octagon(46, 42, W - 92, H - 84, 34);
  ctx.stroke();
  ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
  ctx.lineWidth = 2;
  octagon(68, 64, W - 136, H - 128, 26);
  ctx.stroke();

  ctx.textAlign = "center";
  ctx.fillStyle = "#ffb42a";
  ctx.font = "900 28px 'Courier New', monospace";
  ctx.fillText("DOLOS  //  WASTELAND BINGO", W / 2, 116);

  ctx.fillStyle = "#ffffff";
  ctx.font = "900 74px 'Bebas Neue', 'Arial Narrow', sans-serif";
  ctx.fillText(victoryLabel, W / 2, 224);
  ctx.fillStyle = "#ff7a00";
  ctx.font = "900 44px 'Bebas Neue', 'Arial Narrow', sans-serif";
  ctx.fillText("PLAY  •  MATCH  •  EARN", W / 2, 282);

  octagon(246, 326, 708, 112, 28);
  ctx.fillStyle = "rgba(0, 0, 0, 0.72)";
  ctx.fill();
  ctx.strokeStyle = "#ffae24";
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 42px 'Bebas Neue', 'Arial Narrow', sans-serif";
  ctx.fillText(safeName, W / 2, 374);
  ctx.fillStyle = "#ffb42a";
  ctx.font = "800 24px 'Courier New', monospace";
  ctx.fillText(`${tag}  //  ${score} SYSTEM PTS`, W / 2, 414);

  ctx.textAlign = "left";
  const stats = [
    ["SECTORS MARKED", `${marked}/25`],
    ["CALLS ANSWERED", String(calls)],
    ["VICTORY ROUTE", victoryLabel],
  ];
  stats.forEach(([label, value], i) => {
    const y = 492 + i * 34;
    ctx.fillStyle = "#9b8f82";
    ctx.font = "800 20px 'Courier New', monospace";
    ctx.fillText(label, 270, y);
    ctx.fillStyle = "#ffd36b";
    ctx.textAlign = "right";
    ctx.fillText(value, 930, y);
    ctx.textAlign = "left";
  });

  ctx.textAlign = "center";
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 22px 'Courier New', monospace";
  ctx.fillText("SAME GAME. BIGGER REWARDS.", W / 2, H - 52);

  const link = document.createElement("a");
  link.download = `dolos-bingo-${victoryLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
};
